/**
 * CENTRAL MUSIC TYPE DEFINITIONS
 *
 * This is the single source of truth for all music type related constants,
 * types, labels, and utility functions. When making changes to music types,
 * update only this file.
 *
 * Import from this file rather than defining music type constants elsewhere.
 */

/**
 * Music type options used throughout the application
 */
export const MUSIC_TYPES = {
  NONE: 'none',
  BAND: 'band',
  SUBKREWE_LEVEL: 'subkrewe_level',
  CROWD_LEVEL: 'crowd_level',
  BRING_BAND: 'bring_band',
  OTHER: 'other',
} as const

/**
 * Type definition for music types
 */
export type MusicType = typeof MUSIC_TYPES[keyof typeof MUSIC_TYPES]

/**
 * Display labels for music types
 */
export const MUSIC_TYPE_LABELS: Record<MusicType, string> = {
  [MUSIC_TYPES.NONE]: 'None',
  [MUSIC_TYPES.BAND]: 'We are a band',
  [MUSIC_TYPES.SUBKREWE_LEVEL]: 'Sub-Krewe level - Amplified sound that people can hear just as we roll by',
  [MUSIC_TYPES.CROWD_LEVEL]: 'Crowd-level - Amplified sound that the crowd can hear before we get there',
  [MUSIC_TYPES.BRING_BAND]: 'We bring a band with our SubKrewe',
  [MUSIC_TYPES.OTHER]: 'Other',
}

/**
 * Get the dropdown options for music types
 * @param includeAll Whether to include an "All" option (for filtering)
 * @returns Array of dropdown option objects
 */
export function getMusicTypeOptions(includeAll = false): Array<{ label: string, value: string | null }> {
  const options = Object.entries(MUSIC_TYPE_LABELS).map(([value, label]) => ({
    label,
    value,
  }))

  if (includeAll) {
    options.unshift({ label: 'All Music Types', value: '' })
  }

  return options
}

/**
 * The music type choices for forms; the band option carries a note
 * @returns Array of radio option objects { value: string, label: string, description?: string }
 */
export function getMusicTypeRadioItems(): Array<{ value: string, label: string, description?: string }> {
  return Object.entries(MUSIC_TYPE_LABELS).map(([value, label]) => {
    // Add special description for band option
    if (value === MUSIC_TYPES.BAND) {
      return {
        value,
        label,
        description: 'Note: Band members are exempt from dues. Bands are not visible in the SubKrewe browser by default and require member approval.',
      }
    }
    return {
      value,
      label,
    }
  })
}

/**
 * Get the music type label from its value
 */
export function getMusicTypeLabel(musicType?: string): string {
  if (!musicType) return 'None'
  return MUSIC_TYPE_LABELS[musicType as MusicType] || musicType
}
